import {Injectable} from "@angular/core";
import {FormArray, FormBuilder, FormControl, FormGroup, NonNullableFormBuilder, Validators as AngularValidators, ɵElement} from "@angular/forms";
import {Validators} from "@config/validators.config";
import {
  CompanyIndirectCompanyShareholderForm,
  CompanyIndirectIndividualShareholderForm,
  CompanyIndirectShareholderBaseForm,
  OfficerIndirectPersonForm
} from "@shared/factories/types/form.type";
import {CompanyCapitalInput, GstRegistrationOptionsEnum, OfficerTypeEnum, QuarterEnum} from "@generated/graphql";
import {GstRegistrationDateValidator} from "@utils/validation/app.validation";
import {Company} from "@modules/company/models/company.model";
import {DateTime} from "luxon";
import {parseAnyDate} from "@shared/helpers/date-time.helpers";
import {
  CompanyDetailFormType,
  RegisteredAddressFormType as CompanyIncorporationRegisteredAddressFormType,
  TransferCompanyDetailFormType,
} from "@app/modules/job/models/form/company-detail-form-type";
import {uniqueCompanyNameValidator} from "@shared/helpers/validator.helpers";
import {CompanyCapitalFormType} from "@app/modules/company/models/form/company-capital-form-type";
import {FormStepTwoFormType} from "@app/modules/job/models/form/form-step-two-form-type";
import {ShareholderFormType} from "@app/modules/job/models/form/shareholder-form-type";
import {DirectorFormType} from "@app/modules/job/models/form/director-form-type";
import {OfficerCorporateFormType} from "@app/modules/officer/models/form/officer-corporate-form-type";
import {OfficerIndividualFormType} from "@app/modules/officer/models/form/officer-individual-form-type";

type OfficerBinding = {
  officerType: OfficerTypeEnum;
  officerName: 'officerPerson' | 'officerCompany';
};

type DirectorFormExtraData = {
  dateOfAppointment?: Date | null;
};

type OfficerFormGroup =
  | FormGroup<OfficerIndividualFormType>
  | FormGroup<OfficerCorporateFormType>;

type OfficerFormValue =
  | ReturnType<FormGroup<OfficerIndividualFormType>['getRawValue']>
  | ReturnType<FormGroup<OfficerCorporateFormType>['getRawValue']>;

@Injectable({
  providedIn: 'root'
})
export class FormFactory {
  constructor(
    private readonly fb: FormBuilder,
    private readonly nonNullableFb: NonNullableFormBuilder
  ) {
  }

  companyIndirectShareholderBaseForm(): FormGroup<CompanyIndirectShareholderBaseForm> {
    return this.nonNullableFb.group({
      companyId: this.nonNullableFb.control('', [Validators.required]),
      companyType: this.nonNullableFb.control('', [Validators.required]),
      percentageOfShares: this.nonNullableFb.control(0, [Validators.biggerThan(0), Validators.max(100)]),
    })
  }

  companyIndirectCompanyShareholderForm(): FormGroup<CompanyIndirectCompanyShareholderForm> {
    const {controls} = this.companyIndirectShareholderBaseForm()
    return this.nonNullableFb.group({
      ...controls,
      officerProfile: this.officerIndirectCompanyForm(),
    })
  }

  companyIndirectIndividualShareholderForm(): FormGroup<CompanyIndirectIndividualShareholderForm> {
    const {controls} = this.companyIndirectShareholderBaseForm()
    return this.nonNullableFb.group({
      ...controls,
      officerProfile: this.officerIndirectPersonForm(),
    })
  }

  officerIndirectPersonForm(): FormGroup<OfficerIndirectPersonForm> {
    return this.nonNullableFb.group({
      firstName: this.nonNullableFb.control('', [Validators.required]),
      lastName: this.nonNullableFb.control('', [Validators.required]),
      nationalityId: this.nonNullableFb.control('', [Validators.required]),
      countryId: this.nonNullableFb.control('', [Validators.required]),
      identificationTypeId: this.nonNullableFb.control('', [Validators.required]),
      identificationNumber: this.nonNullableFb.control('', [Validators.required]),
    })
  }

  officerIndirectCompanyForm(): FormGroup<{
    [K in keyof {
      name: FormControl<string>;
      registrationNumber: FormControl<string>;
      registrationDate: FormControl<Date>;
      countryId: FormControl<string>
    }]: ɵElement<{
      name: FormControl<string>;
      registrationNumber: FormControl<string>;
      registrationDate: FormControl<Date>;
      countryId: FormControl<string>
    }[K], never>
  }> {
    return this.nonNullableFb.group({
      name: this.nonNullableFb.control('', [Validators.required]),
      countryId: this.nonNullableFb.control('', [Validators.required]),
      registrationNumber: this.nonNullableFb.control('', [Validators.required]),
      registrationDate: this.nonNullableFb.control<Date>(new Date(Date.now()), [Validators.required])
    })
  }

  registerGstForm(company: Company) {
    return this.nonNullableFb.group({
      effectiveDate: this.nonNullableFb.control<Date>(DateTime.now().toJSDate(), [Validators.required, GstRegistrationDateValidator(parseAnyDate(company.registrationDate))]),
      companyId: this.nonNullableFb.control<string>(null, [Validators.required]),
      value: this.nonNullableFb.control<GstRegistrationOptionsEnum>(GstRegistrationOptionsEnum.RegisterGst, [Validators.required]),
      jobId: this.fb.control(null),
    })
  }

  requestGstReturnForm() {
    return this.nonNullableFb.group({
      isUsingTenantAccountingService: this.nonNullableFb.control<boolean>(false, [Validators.required]),
      needToPrepareDocument: this.nonNullableFb.control<boolean>(false, [Validators.required]),
      year: this.nonNullableFb.control<number>(null, [Validators.required]),
      quarter: this.nonNullableFb.control<QuarterEnum>(null, [Validators.required]),
      file: this.fb.control<File | null>(null, [Validators.required]),
    })
  }

  gstReturnForm() {
    const {controls} = this.requestGstReturnForm()

    return this.nonNullableFb.group({
      effectiveDate: this.nonNullableFb.control<Date>(DateTime.now().toJSDate(), [Validators.required]),
      companyId: this.nonNullableFb.control<string>('', [Validators.required]),
      jobId: this.nonNullableFb.control<string>('', [Validators.required]),
      gstReturnDetail: this.fb.group({
        ...controls,
        requestedFile: this.fb.control<File | null>(null)
      })
    })
  }

  companyIncorporationRegisteredAddressForm(
    countryId: string,
  ): FormGroup<CompanyIncorporationRegisteredAddressFormType> {
    return this.fb.group<CompanyIncorporationRegisteredAddressFormType>({
      address: this.fb.control('', [AngularValidators.required]),
      apartment: this.fb.control(''),
      city: this.fb.control(''),
      state: this.fb.control(''),
      zip: this.fb.control(''),
      fromDate: this.fb.control(DateTime.now().toJSDate()),
      countryId: this.fb.control(
        {value: countryId, disabled: true},
        [AngularValidators.required],
      ),
      rentalAgreementDocument: this.fb.control<File | null>(null),
    });
  }

  registerCompanyDetailForm(
    countryId: string,
    registeredAddressForm: FormGroup<CompanyIncorporationRegisteredAddressFormType>,
  ): FormGroup<CompanyDetailFormType> {
    return this.fb.group<CompanyDetailFormType>(
      {
        name: this.fb.control('', AngularValidators.required),
        secondName: this.fb.control('', AngularValidators.required),
        thirdName: this.fb.control('', [AngularValidators.required]),
        website: this.fb.control(''),
        companyActivity: this.fb.control('', [AngularValidators.required]),
        fyeDate: this.fb.control(this.getEndOfYearDate(), [
          AngularValidators.required,
        ]),
        countryId: this.fb.control(countryId),
        registeredAddress: registeredAddressForm,
      },
      {
        validators: uniqueCompanyNameValidator,
      },
    );
  }

  transferCompanyDetailForm(
    countryId: string,
    registeredAddressForm: FormGroup<CompanyIncorporationRegisteredAddressFormType>,
  ): FormGroup<TransferCompanyDetailFormType> {
    return this.fb.group<TransferCompanyDetailFormType>({
      countryId: this.fb.control(countryId),
      name: this.fb.control('', [AngularValidators.required]),
      website: this.fb.control(''),
      companyActivity: this.fb.control('', [AngularValidators.required]),
      fyeDate: this.fb.control(this.getEndOfYearDate(), [AngularValidators.required]),
      registrationNumber: this.fb.control('', [AngularValidators.required]),
      registrationDate: this.fb.control(null, [AngularValidators.required]),
      numberOfEmployees: this.fb.control(0),
      specialLicenseDocument: this.fb.control<File | null>(null),
      registeredAddress: registeredAddressForm,
    });
  }

  private getEndOfYearDate(): Date {
    return DateTime.now().endOf('year').toJSDate();
  }

  companyCapitalArrayForm(): FormArray<FormGroup<CompanyCapitalFormType>> {
    return this.fb.array<FormGroup<CompanyCapitalFormType>>(
      [],
      AngularValidators.required,
    );
  }

  companyCapitalItemForm(
    input?: Partial<CompanyCapitalInput>,
  ): FormGroup<CompanyCapitalFormType> {
    return this.fb.group<CompanyCapitalFormType>({
      totalAmount: this.fb.control(input?.totalAmount ?? 0, [
        AngularValidators.required,
        AngularValidators.min(1),
      ]),
      totalShares: this.fb.control(input?.totalShares ?? 0, [
        AngularValidators.required,
        AngularValidators.min(1),
      ]),
      currency: this.fb.control(input?.currency ?? '', AngularValidators.required),
      shareTypeId: this.fb.control(
        {value: input?.shareTypeId ?? '', disabled: true},
        [AngularValidators.required],
      ),
    });
  }

  companyShareholderArrayForm(): FormArray<FormGroup<ShareholderFormType>> {
    return this.fb.array<FormGroup<ShareholderFormType>>(
      [],
      AngularValidators.required,
    );
  }

  companyShareholderGroup({
    binding,
    id,
    officerValue,
    refId,
    shareholdingDetails,
  }: {
    binding: OfficerBinding;
    id?: string;
    officerValue: OfficerFormGroup | OfficerFormValue;
    refId?: string;
    shareholdingDetails: FormArray;
  }): FormGroup<ShareholderFormType> {
    return this.fb.group<ShareholderFormType>({
      id: this.fb.control(id),
      officerType: this.fb.control(binding.officerType),
      isNominee: this.fb.control(false),
      [binding.officerName]: officerValue,
      shareholdingDetails,
      refId: this.fb.control(refId),
    });
  }

  companyDirectorArrayForm(): FormArray<FormGroup<DirectorFormType>> {
    return this.fb.array<FormGroup<DirectorFormType>>(
      [],
      AngularValidators.required,
    );
  }

  companyDirectorGroup({
    binding,
    id,
    officerValue,
    refId,
    extraData,
  }: {
    binding: OfficerBinding;
    id?: string;
    officerValue: OfficerFormGroup | OfficerFormValue;
    refId?: string;
    extraData?: DirectorFormExtraData;
  }): FormGroup<DirectorFormType> {
    const input = this.fb.group<DirectorFormType>({
      id: this.fb.control(id),
      officerType: this.fb.control(binding.officerType),
      isNominee: this.fb.control(false),
      dateFrom: this.fb.control(DateTime.now().toJSDate()),
      [binding.officerName]: officerValue,
      refId: this.fb.control(refId),
    });

    if (extraData?.dateOfAppointment) {
      input.controls.dateOfAppointment = this.fb.control(
        extraData.dateOfAppointment,
      );
    }

    return input;
  }

  companyNomineeDirectorGroup(countryId: string): FormGroup<DirectorFormType> {
    return this.fb.group<DirectorFormType>({
      officerType: this.fb.control(OfficerTypeEnum.Individual),
      isNominee: this.fb.control(true),
      dateFrom: this.fb.control(DateTime.now().toJSDate()),
      officerPerson: this.fb.group<OfficerIndividualFormType>({
        firstName: this.fb.control('Nominee'),
        countryId: this.fb.control(countryId),
      }),
    });
  }

  companyIncorporationStructureForm(
    companyCapitals: FormArray<FormGroup<CompanyCapitalFormType>>,
    shareholders: FormArray<FormGroup<ShareholderFormType>>,
    directors: FormArray<FormGroup<DirectorFormType>>,
  ): FormGroup<FormStepTwoFormType> {
    return this.fb.group<FormStepTwoFormType>({
      companyCapitals,
      shareholders,
      directors,
    });
  }
}
